import React, { useEffect, useState, useCallback } from "react";
import { dashboardAPI } from "../../services/api/dashboard";
import { useAgent } from "../../contexts/AgentContext";
import NeedsAttention from "./NeedsAttention";
import Pagination, { paginate } from "./Pagination";
import { timeAgo } from "./utils";

const WINDOWS = [7, 30, 90];

const STATUS_STYLES = {
  completed: "bg-emerald-50 text-emerald-600",
  running: "bg-blue-50 text-blue-600",
  failed: "bg-rose-50 text-rose-600",
  pending: "bg-gray-100 text-gray-500",
};

export default function DashboardActivity({ clientId, navigate }) {
  const { agents = [] } = useAgent();
  const [days, setDays] = useState(7);
  const [agentId, setAgentId] = useState("");
  const [summary, setSummary] = useState(null);
  const [simPage, setSimPage] = useState(0);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await dashboardAPI.getSummary({ clientId, days, agentId: agentId || undefined });
      setSummary(res.data);
    } catch (_) {
      setSummary(null);
    }
  }, [clientId, days, agentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset the recent-simulations page when the filtered result set changes.
  useEffect(() => {
    setSimPage(0);
  }, [days, agentId]);

  const sims = summary?.recent_simulations || [];
  const { slice: simSlice, safePage: simSafePage, pageCount: simPageCount } = paginate(sims, simPage);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Activity & health</h2>
        <div className="flex items-center gap-2">
          <select
            aria-label="agent"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-primary/20 focus:border-primary max-w-[10rem]"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setDays(w)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  days === w ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-gray-50"
                }`}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent simulations + needs attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <div className="bg-card rounded-xl border border-border p-5 h-full flex flex-col">
          <h3 className="text-sm font-semibold text-foreground mb-3">Recent simulations</h3>
          {sims.length === 0 ? (
            <button onClick={() => navigate("/batch-run/new")}
              className="text-sm text-muted-foreground hover:text-primary flex-1 text-left">
              No simulations yet — run a batch test →
            </button>
          ) : (
            <ul className="divide-y divide-border flex-1">
              {simSlice.map((s) => (
                <li key={s.id}>
                  <button onClick={() => navigate(`/batch-run/${s.id}`)}
                    className="w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 rounded px-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.agent_name || "—"} · {timeAgo(s.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{s.pass_count} / {s.run_count}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || STATUS_STYLES.pending}`}>
                        {s.status}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Pagination
            page={simSafePage}
            pageCount={simPageCount}
            onPrev={() => setSimPage((p) => Math.max(0, p - 1))}
            onNext={() => setSimPage((p) => p + 1)}
          />
        </div>

        <NeedsAttention
          clientId={clientId}
          days={days}
          agentId={agentId || undefined}
          failedCalls={summary?.failed_calls || []}
          navigate={navigate}
        />
      </div>
    </div>
  );
}
